<template>
  <div class="menu-carousel">
    <div
      ref="carouselContainer"
      class="
        menu-carousel__carousel
        splide
      "
      :js-carousel="`wrapper-${namespace}`"
    >
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(slide, index) of slides"
            :key="index"
            class="splide__slide"
          >
            <a
              class="menu-carousel__slide-link"
              :href="slide.buttonUrl"
            >
              <art-direction
                :image="{
                  url: slide.slideImage
                }"
                :padding="false"
                ratio="image"
              />
              <div
                v-if="!isMobile"
                class="menu-carousel__slide-action"
              >
                <btn
                  v-if="slide.buttonLabel"
                  modifiers="text"
                  show-button-icon
                  :url="slide.buttonUrl"
                  :label="slide.buttonLabel"
                />
              </div>
            </a>
            <div
              v-if="isMobile"
              class="
                menu-carousel__slide-action
                menu-carousel__slide-action--mobile
              "
            >
              <btn
                v-if="slide.buttonLabel"
                modifiers="text"
                show-button-icon
                :url="slide.buttonUrl"
                :label="slide.buttonLabel"
              />
            </div>
          </li>
        </ul>
      </div>

      <div
        class="splide__arrows"
        :class="{
          [$class('active')]: arrowsHovered
        }"
      >
        <button
          v-show="formattedCarouselIndex > 1"
          class="splide__arrow splide__arrow--prev"
          type="button"
          @mouseenter="() => arrowsHovered = true"
          @mouseleave="() => arrowsHovered = false"
          v-text="$string('accessibility.carousel.previous')"
        />

        <span
          v-if="carousel"
          class="splide__slide-counter"
          v-text="$string('accessibility.carousel.slide', {
            replace: {
              page: formattedCarouselIndex,
              total: formattedTotal
            }
          })"
        />

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
          @mouseenter="() => arrowsHovered = true"
          @mouseleave="() => arrowsHovered = false"
          v-html="$string('accessibility.carousel.next')"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu carousel (menu-carousel)
 * -----------------------------------------------------------------------------
 * Carousel of clickable slides that displays within the mega menu.
 *
 * @param {Number} [autoplaySpeed] - Number of secs between slides on carousel.
 * @param {String} namespace - Unique namespace for generating a carousel in.
 * @param {Array} slides - Array of slide objects.
 *
 */
import { nextTick } from 'vue'

import { mapState } from 'vuex'

import Carousel from '~/api/carousel'

import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'

export default {
  name: 'MenuCarousel',

  components: {
    ArtDirection,
    Btn,
  },

  props: {
    autoplaySpeed: {
      type: Number,
      default: 3500,
    },

    namespace: {
      type: String,
      default: '',
    },

    slides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      arrowsHovered: false,
      carousel: null,
      carouselIndex: 0,
      splideOptions: {
        arrows: true,
        gap: '0',
        perMove: 1,
        perPage: 1,
        speed: 200,
        type: 'fade',
      },
      splideOptionsMobile: {
        drag: 'free',
        fixedWidth: '70vw',
        gap: '0',
        perMove: 1,
        perPage: 1,
        snap: true,
        type: 'slide',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Grabs the current namespace.
     * @returns {String}
     */
    carouselNamespace() {
      return this.namespace
        ? `[js-carousel="wrapper-${this.namespace}"]`
        : false
    },

    formattedCarouselIndex() {
      const displayPage = this.carouselIndex + 1
      return displayPage < 10
        ? `0${displayPage}`
        : displayPage
    },

    formattedTotal() {
      return this.slides.length < 10
        ? `0${this.slides.length}`
        : this.slides.length
    },
  },

  mounted() {
    this.createCarousel()
  },

  methods: {

    /**
     * Main carousel contructor function
     */
    createCarousel() {
      if (!this.namespace) {
        return
      }

      nextTick().then(() => {
        const container = this.$refs.carouselContainer

        if (!container) {
          return
        }

        const reducedMotion =
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const interval = this.autoplaySpeed
        const autoplay = Boolean(interval && !reducedMotion)
        const options = this.isMobile
          ? this.splideOptionsMobile
          : this.splideOptions

        this.carousel = Carousel(this.carouselNamespace, {
          ...options,
          rewind: true,
          autoplay,
          interval,
        })

        /**
         * When a slide updates, save the index to data.
         */
        this.carousel.on('move', (index) => {
          this.carouselIndex = index
        })

        this.carousel.mount()
      })
    },
  },
}
</script>

<style lang="scss">
@import './menu-carousel';
</style>
